import axios from 'axios';
import config from '../Config';
import authHeader from './auth-header';
import { handle401Unauthorized } from '../utils/Helper';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for DieCategory module to handle 401 errors
const dieCategoryAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for DieCategory module
dieCategoryAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// DieCategory APIs
const getDieCategoryList = (page, pagesize, search, ordering) => {
  return dieCategoryAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/die-category/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDieCategoryById = id => {
  return dieCategoryAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-category/${id}/`, {
    headers: authHeader(),
  });
};

const addDieCategory = formData => {
  return dieCategoryAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-category/`,
    formData,
    { headers: authHeader() },
  );
};

const updateDieCategory = (id, formPayload) => {
  return dieCategoryAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/die-category/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchDieCategory = (id, formPayload) => {
  return dieCategoryAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/die-category/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const deleteDieCategory = id => {
  return dieCategoryAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/die-category/${id}/`,
    { headers: authHeader() },
  );
};

const getDieCategoryDropdown = (filterKey) => {
  return dieCategoryAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-category/dropdown/`, {
    headers: authHeader(),
    params: {
      ...(filterKey && { filter_key: filterKey }),
    },
  });
};

const getDieCategoryArchiveList = (page, pagesize, search, ordering) => {
  return dieCategoryAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-category/archived/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDieCategoryArchiveById = id => {
  return dieCategoryAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-category-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restoreDieCategory = ids => {
  return dieCategoryAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-category/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

const bulkArchiveDieCategory = ids => {
  return dieCategoryAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-category/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

// Bulk Import APIs
const bulkImportDieCategory = formData => {
  return dieCategoryAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-category/bulk-import/`,
    formData,
    {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

const getImportLogs = (page, pagesize) => {
  return dieCategoryAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-category/import-logs/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const getImportErrors = (importLogId, page, pagesize) => {
  return dieCategoryAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-category/import-errors/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const downloadErrorReport = importLogId => {
  return dieCategoryAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-category/error-report/download/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
      },
      responseType: 'blob',
    },
  );
};

const dieCategoryService = {
  getDieCategoryList,
  getDieCategoryById,
  addDieCategory,
  updateDieCategory,
  patchDieCategory,
  deleteDieCategory,
  getDieCategoryDropdown,
  getDieCategoryArchiveList,
  getDieCategoryArchiveById,
  restoreDieCategory,
  bulkArchiveDieCategory,
  bulkImportDieCategory,
  getImportLogs,
  getImportErrors,
  downloadErrorReport,
};

export default dieCategoryService;
